// The record files, parsed off the main thread and only when asked.
//
// An export writes one record tree per document: every element with its
// quantities, prices and parameters. On SpaceX BA03 that is 51 files and
// more than 1.5 GB of JSON, and the viewer used to `await` all of them,
// on the main thread, BEFORE the 3D was even constructed. The parse
// alone froze the tab for tens of seconds and left ~2 GB of objects
// alive for the whole session.
//
// Nothing needs those objects at open. The tree comes from the
// exporter's `<Document>_TreeIndex.json.gz` (a few KB); the full records
// answer three questions, all of them user-driven:
//
//   expand   the elements under one aggregated component row
//   find     the record behind a UniqueId the user clicked in 3D
//   params   one element's parameters, from the per-category slice
//
// So they live here, in a worker, fetched and parsed the first time a
// question needs them and kept for the rest of the session. The main
// thread never holds a record tree.
//
// Webpack 4 (react-scripts 3) has no worker loader, so the worker is a
// Blob built from the source string below. It has to be self-contained:
// no imports, no closures over this module.

const WORKER_SOURCE = `
var docs = {};    // docId -> { url, promise, byUid, byKey, chunks }
var slices = {};  // docId|token -> { url, promise, byUid }

// === Chunked record trees (2026-09-13) ===
//
// A whole record tree can be too big for any browser (the SpaceX host is
// 1.5 GB of JSON), so the exporter can also write it as pieces:
//   <Document>_TreeChunk.<idx>.json.gz   the same shape, only the
//                                        Components whose UniqueId hashes
//                                        to <idx>
// and say so in the _TreeIndex:
//   recordChunks: { hash: 'fnv1a32', size, count, byKey: { 'Category|Group|Name': [idx...] }, probe: { uid, chunk } }
// find = one fetch, the chunk the uid hashes to; expand = the chunks
// byKey lists for that row.
//
// A hash split scatters every category across every chunk (on the
// SpaceX host each real category lists 39-45 of 45 chunks), so an expand
// that fetched "all the chunks for the key" would read the whole 1.5 GB
// tree through the side door and defeat the size guard. Two budgets keep
// a chunked document inside what a browser can hold:
//   MAX_EXPAND_CHUNKS   pieces one expand may fetch. It stops early once
//                       it has a page of rows; a scattered small
//                       category shows what those pieces held plus
//                       "N more, pick them in the model".
//   MAX_RESIDENT_CHUNKS pieces kept parsed per document; beyond it the
//                       least recently used piece is dropped, so forty
//                       clicks on forty elements do not accumulate forty
//                       pieces.
// Pieces are therefore kept SEPARATELY (one byUid/byKey pair each) and
// looked up in turn, never merged into one map that could only grow.
// An expand answer says whether its uid list is complete; the tree only
// lets a row act on "all its elements" when it is.
//
// FNV-1a, 32-bit, over the UTF-8 bytes of the UniqueId as stored:
// offset 2166136261, prime 16777619, unsigned, then modulo count. The
// index carries a probe (a real uid and its chunk) and a document whose
// probe does not agree is treated as unchunked - a wrong hash would
// otherwise answer "not found" for every click, silently.
function fnv1a32(str) {
  var h = 0x811c9dc5;
  for (var i = 0; i < str.length; i++) {
    var c = str.charCodeAt(i);
    var bytes;
    if (c < 0x80) bytes = [c];
    else if (c < 0x800) bytes = [0xc0 | (c >> 6), 0x80 | (c & 0x3f)];
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < str.length) {
      var lo = str.charCodeAt(++i);
      var cp = 0x10000 + ((c - 0xd800) << 10) + (lo - 0xdc00);
      bytes = [0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 0x3f), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f)];
    } else bytes = [0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f)];
    for (var b = 0; b < bytes.length; b++) {
      h ^= bytes[b];
      // h * 16777619 mod 2^32, without losing bits to doubles.
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
  }
  return h >>> 0;
}

function chunkOf(uid, count) {
  return fnv1a32(String(uid)) % count;
}

// HOW MUCH ONE EXPAND MAY DOWNLOAD, IN BYTES - NOT IN PIECES.
//
// It was four pieces, and that number quietly encoded an assumption about
// how big a piece is. When the exporter's splitter was fixed on 2026-10-07
// the host went from 4 pieces of 16.3 MB to 8 of 8.3 MB - the same tree, the
// same total, half the piece - and a count budget of four then covered HALF
// the scatter instead of all of it. Every row whose elements spread across
// all the pieces (which on this host is every real row: the live index lists
// all pieces for each key) would have come back with uidsComplete false, so
// the tree would have stopped letting anybody act on "all the elements of
// this row". The record error would have gone away and that would have gone
// with it, silently, as a consequence of a fix somewhere else.
//
// A byte budget does not care how the tree was cut. 80 MB compressed holds
// today's worst case either way - 4 x 16.3 = 65 MB, or 8 x 8.3 = 67 MB - and
// a future export cut into twenty small pieces is covered too.
var MAX_EXPAND_BYTES = 80 * 1024 * 1024;
// A loop guard, not a budget. Nothing should reach it; if it does, the
// chunking is pathological and stopping is better than reading for ever.
var MAX_EXPAND_CHUNKS = 32;
var MAX_RESIDENT_CHUNKS = 6;
var clock = 0;

// Accept a document's chunk description, or explain why not.
function acceptChunks(spec) {
  if (!spec || typeof spec !== 'object') return null;
  var count = Number(spec.count) || 0;
  var urls = spec.urls || {};
  if (count < 1 || spec.hash !== 'fnv1a32') return null;
  for (var i = 0; i < count; i++) if (!urls[i]) return null;
  if (spec.probe && spec.probe.uid !== undefined && chunkOf(spec.probe.uid, count) !== Number(spec.probe.chunk)) return null;
  // byKey entries are chunk indexes, or [index, rowCount] pairs when the
  // exporter knows how many rows of the key each piece holds; pairs let an
  // expand fetch the richest pieces first.
  var byKey = {};
  var raw = spec.byKey || {};
  for (var k in raw) {
    if (!Object.prototype.hasOwnProperty.call(raw, k) || !Array.isArray(raw[k])) continue;
    var list = raw[k].map(function (e) {
      return Array.isArray(e) ? { idx: Number(e[0]), rows: Number(e[1]) || 0 } : { idx: Number(e), rows: -1 };
    }).filter(function (e) { return e.idx >= 0 && e.idx < count; });
    list.sort(function (a, b) { return b.rows - a.rows; });
    byKey[k] = list;
  }
  return { count: count, urls: urls, byKey: byKey, loaded: {} };
}

function keyOf(section, category, group, name) {
  return section + '|' + category + '|' + group + '|' + name;
}

// Same walk as helpers/three.js generateRawMeshes, but building the two
// lookups the viewer asks for instead of one flat array.
function indexRecords(json) {
  var byUid = new Map();
  var byKey = new Map();
  var sections = [
    ['S', json.SubProjects],
    ['E', json.ExistingSubProjects],
    ['D', json.DemolitionSubprojects],
    ['X', json.DisabledSubprojects],
  ];
  for (var s = 0; s < sections.length; s++) {
    var tag = sections[s][0];
    var subprojects = sections[s][1] || [];
    for (var i = 0; i < subprojects.length; i++) {
      var sub = subprojects[i];
      var groups = sub.Groups || [];
      for (var g = 0; g < groups.length; g++) {
        var group = groups[g];
        var comps = group.Components || [];
        for (var c = 0; c < comps.length; c++) {
          var comp = comps[c];
          var k = keyOf(tag, sub.Category, group.Name, comp.Name);
          var bucket = byKey.get(k);
          if (!bucket) { bucket = []; byKey.set(k, bucket); }
          bucket.push(comp);
          if (comp.UniqueId) byUid.set(comp.UniqueId, { comp: comp, key: k });
        }
      }
    }
  }
  return { byUid: byUid, byKey: byKey };
}

// How big a record file this worker will parse.
//
// A dedicated worker shares the renderer process with the page. When the
// SpaceX BA03 host tree came through at 1.5 GB of JSON (2026-09-13), the
// first click on a host element made Response.json() buffer the body,
// decode it into a string at V8's ceiling and build the graph on top of a
// page already holding 3 GB of streamed geometry - and Chrome took the
// whole tab down. Nothing catches that; it has to be refused before the
// parse. Two ceilings, because the two facts arrive at different times:
// Content-Length (the compressed object size, readable cross-origin and
// there before a byte of body) and the raw byte count as the body streams
// in. The largest record file known to open fine is ~12 MB / ~300 MB.
var MAX_COMPRESSED_BYTES = 16 * 1024 * 1024;
var MAX_RAW_BYTES = 512 * 1024 * 1024;
var TOO_LARGE = 'RecordFileTooLarge';

function tooLarge(compressed, raw) {
  var mb = function (n) { return (n / 1048576).toFixed(n >= 104857600 ? 0 : 1) + ' MB'; };
  var what = compressed ? mb(compressed) + ' compressed' : 'over ' + mb(raw) + ' of JSON';
  var err = new Error(TOO_LARGE + ': ' + what);
  err.code = TOO_LARGE;
  err.compressed = compressed || 0;
  err.raw = raw || 0;
  return err;
}

// Read a body with a byte cap instead of r.json(): the cap is checked per
// chunk, so a file that is too big is abandoned after MAX_RAW_BYTES of
// download, not after all of it is sitting in memory.
function readCapped(r) {
  if (!r.body || !r.body.getReader) {
    return r.text().then(function (text) {
      if (text.length > MAX_RAW_BYTES) throw tooLarge(0, text.length);
      return text;
    });
  }
  var reader = r.body.getReader();
  var chunks = [];
  var total = 0;
  var step = function () {
    return reader.read().then(function (res) {
      if (res.done) return chunks;
      total += res.value.byteLength;
      if (total > MAX_RAW_BYTES) {
        try { reader.cancel(); } catch (e) { /* already closed */ }
        throw tooLarge(0, total);
      }
      chunks.push(res.value);
      return step();
    });
  };
  return step().then(function (parts) {
    var decoder = new TextDecoder('utf-8');
    var text = '';
    for (var i = 0; i < parts.length; i++) text += decoder.decode(parts[i], { stream: true });
    text += decoder.decode();
    return text;
  });
}

function fetchJson(url, onSize) {
  return fetch(url, { mode: 'cors' }).then(function (r) {
    if (!r.ok) throw new Error('HTTP ' + r.status + ' for ' + url);
    var declared = Number(r.headers && r.headers.get ? r.headers.get('content-length') : 0) || 0;
    // The size the expand budget spends. Reported even when the header is
    // absent (0), because a piece whose size cannot be read must not be
    // counted as free.
    if (typeof onSize === 'function') onSize(declared);
    if (declared > MAX_COMPRESSED_BYTES) {
      try { if (r.body && r.body.cancel) r.body.cancel(); } catch (e) { /* nothing to cancel */ }
      throw tooLarge(declared, 0);
    }
    return readCapped(r).then(function (text) { return JSON.parse(text); });
  });
}

// A refused file stays refused for the session - asking again would only
// download it again. Any other failure (network, a 5xx) is forgotten so
// the next question can retry.
function remember(entry, promise) {
  entry.promise = promise.catch(function (err) {
    if (!err || err.code !== TOO_LARGE) entry.promise = null;
    throw err;
  });
  return entry.promise;
}

// One piece of a chunked tree, parsed once and merged into the document's
// maps. Pieces carry the same section arrays as the whole tree, so the
// same indexRecords walk applies.
function evictChunks(ch, keep) {
  var idxs = Object.keys(ch.loaded).filter(function (i) { return ch.loaded[i].maps && !keep[i]; });
  while (idxs.length && Object.keys(ch.loaded).filter(function (i) { return ch.loaded[i].maps; }).length > MAX_RESIDENT_CHUNKS) {
    idxs.sort(function (a, b) { return ch.loaded[a].used - ch.loaded[b].used; });
    delete ch.loaded[idxs.shift()];
  }
}

// One piece of a chunked tree: fetched once, parsed into its own maps,
// touched on every use so eviction knows the order. keep names the
// pieces the current question still needs, which eviction must not drop.
function ensureChunk(entry, idx, keep) {
  var ch = entry.chunks;
  var slot = ch.loaded[idx];
  if (!slot) {
    slot = { url: ch.urls[idx], used: ++clock, maps: null, bytes: 0 };
    remember(slot, fetchJson(slot.url, function (n) { slot.bytes = n; }).then(function (json) {
      slot.maps = indexRecords(json);
      evictChunks(ch, keep || {});
      return slot;
    }));
    ch.loaded[idx] = slot;
  }
  slot.used = ++clock;
  return slot.promise.then(function () { return slot; });
}

function chunkBucket(slot, key) {
  return (slot && slot.maps && slot.maps.byKey.get(key)) || [];
}

// The exporter's byKey has no section tag; the tree's composite key does
// (S|Category|Group|Name). Strip the tag to look the chunks up.
function chunksForKey(entry, key) {
  var k = String(key);
  var bare = k.length > 2 && k.charAt(1) === '|' && 'SEDX'.indexOf(k.charAt(0)) >= 0 ? k.slice(2) : k;
  var list = entry.chunks.byKey[bare];
  if (!Array.isArray(list)) return null;
  return list;
}

// Expand on a chunked document: fetch pieces for the key, richest first,
// resident ones first of all, and stop once a page of rows is in hand or
// the per-expand budget is spent. The total comes from the index
// (expectedTotal), so the row can say how many it stands for without the
// worker having seen them all.
function expandChunked(entry, key, expectedTotal) {
  var list = chunksForKey(entry, key);
  if (!list) return null;
  var ch = entry.chunks;
  var resident = list.filter(function (e) { return ch.loaded[e.idx] && ch.loaded[e.idx].maps; });
  var pending = list.filter(function (e) { return !(ch.loaded[e.idx] && ch.loaded[e.idx].maps); });
  var order = resident.concat(pending);
  var keep = {};
  var rows = [];
  var fetchedNow = 0;
  var bytesNow = 0;
  var i = 0;
  var step = function () {
    if (i >= order.length) return Promise.resolve();
    var e = order[i++];
    var isResident = !!(ch.loaded[e.idx] && ch.loaded[e.idx].maps);
    if (!isResident) {
      // A RESIDENT PIECE IS FREE: it is already downloaded and parsed, so it
      // spends neither budget. Only a fetch does.
      if (fetchedNow >= MAX_EXPAND_CHUNKS || rows.length >= MAX_ROWS) return Promise.resolve();
      // Spent AFTER the first fetch, so one piece is always read however big
      // it is - a budget that can refuse the only piece a row lives in would
      // answer "no elements here" for a row that has them.
      if (fetchedNow > 0 && bytesNow >= MAX_EXPAND_BYTES) return Promise.resolve();
      fetchedNow += 1;
    }
    keep[e.idx] = true;
    return ensureChunk(entry, e.idx, keep).then(function (slot) {
      if (!isResident) bytesNow += Number(slot.bytes) || 0;
      var bucket = chunkBucket(slot, key);
      for (var b = 0; b < bucket.length; b++) rows.push(bucket[b]);
      return step();
    });
  };
  return step().then(function () {
    var covered = list.every(function (e) { return keep[e.idx]; });
    var uids = new Array(rows.length);
    for (var u = 0; u < rows.length; u++) uids[u] = rows[u].UniqueId;
    var total = Math.max(Number(expectedTotal) || 0, covered ? rows.length : rows.length);
    return {
      components: rows.length > MAX_ROWS ? rows.slice(0, MAX_ROWS) : rows,
      uids: uids,
      total: total,
      truncated: total > Math.min(rows.length, MAX_ROWS) || !covered,
      uidsComplete: covered,
      chunksRead: Object.keys(keep).length,
    };
  });
}

function ensureDoc(docId) {
  var entry = docs[docId];
  if (!entry) throw new Error('unknown document ' + docId);
  if (!entry.promise) {
    remember(entry, fetchJson(entry.url).then(function (json) {
      var built = indexRecords(json);
      entry.byUid = built.byUid;
      entry.byKey = built.byKey;
      return entry;
    }));
  }
  return entry.promise;
}

function ensureSlice(docId, token) {
  var id = docId + '|' + token;
  var entry = slices[id];
  if (!entry) return Promise.resolve(null);
  if (!entry.promise) {
    remember(entry, fetchJson(entry.url).then(function (rows) {
      var byUid = new Map();
      var list = Array.isArray(rows) ? rows : [];
      for (var i = 0; i < list.length; i++) {
        var row = list[i];
        if (row && row.UniqueId && !byUid.has(row.UniqueId)) byUid.set(row.UniqueId, row);
      }
      entry.byUid = byUid;
      return entry;
    }));
  }
  return entry.promise;
}

// A group of 213,850 electrical fixtures is not a tree the browser can
// render, and never was: the rows themselves are what used to lock the
// page up. Hand back a capped page of records for display and the full
// uid list separately, so hiding or selecting the row still acts on
// every element under it.
var MAX_ROWS = 2000;

function expandBucket(entry, key) {
  var bucket = entry.byKey.get(key) || [];
  var uids = new Array(bucket.length);
  for (var i = 0; i < bucket.length; i++) uids[i] = bucket[i].UniqueId;
  return {
    components: bucket.length > MAX_ROWS ? bucket.slice(0, MAX_ROWS) : bucket,
    uids: uids,
    total: bucket.length,
    truncated: bucket.length > MAX_ROWS,
  };
}

function onExpand(msg) {
  var entry = docs[msg.doc];
  if (entry && entry.chunks) {
    var chunked = expandChunked(entry, msg.key, msg.expectedTotal);
    if (chunked) return chunked;
    // Not in byKey at all: the index predates this row, fall through to
    // the whole tree (which the size guard may refuse).
  }
  return ensureDoc(msg.doc).then(function (entry) {
    var bucket = entry.byKey.get(msg.key) || [];
    var uids = new Array(bucket.length);
    for (var i = 0; i < bucket.length; i++) uids[i] = bucket[i].UniqueId;
    return {
      components: bucket.length > MAX_ROWS ? bucket.slice(0, MAX_ROWS) : bucket,
      uids: uids,
      total: bucket.length,
      truncated: bucket.length > MAX_ROWS,
    };
  });
}

function onFind(msg) {
  var entry = docs[msg.doc];
  if (entry && entry.chunks) {
    var idx = chunkOf(msg.uid, entry.chunks.count);
    var keep = {}; keep[idx] = true;
    return ensureChunk(entry, idx, keep).then(function (slot) {
      var hit = slot.maps && slot.maps.byUid.get(msg.uid);
      if (!hit) return null;
      return { component: hit.comp, key: hit.key };
    });
  }
  return ensureDoc(msg.doc).then(function (entry) {
    var hit = entry.byUid.get(msg.uid);
    if (!hit) return null;
    return { component: hit.comp, key: hit.key };
  });
}

// Parameters for one element. The exporter splits _AdditionalParam by
// top category; the rows an element record does not list land in
// "Other". Try the element's own category first, then Other, then the
// unsplit file if this export predates the slices.
function onParams(msg) {
  var tokens = [];
  if (msg.token) tokens.push(msg.token);
  if (tokens.indexOf('Other') === -1) tokens.push('Other');
  if (msg.fallbackToken) tokens.push(msg.fallbackToken);
  var step = function (i) {
    if (i >= tokens.length) return Promise.resolve(null);
    return ensureSlice(msg.doc, tokens[i]).then(function (entry) {
      if (entry && entry.byUid) {
        var row = entry.byUid.get(msg.uid);
        if (row) return row;
      }
      return step(i + 1);
    });
  };
  return step(0);
}

self.onmessage = function (e) {
  var msg = e.data || {};
  var done = function (payload) { self.postMessage({ id: msg.id, ok: true, payload: payload }); };
  var fail = function (err) { self.postMessage({ id: msg.id, ok: false, error: String((err && err.message) || err) }); };
  try {
    if (msg.op === 'register') {
      docs[msg.doc] = docs[msg.doc] || { url: msg.url };
      docs[msg.doc].url = msg.url;
      // Chunks are preferred whenever the index describes them and the
      // probe agrees; otherwise the whole tree, as before.
      docs[msg.doc].chunks = acceptChunks(msg.chunks);
      var tokens = msg.slices || {};
      for (var token in tokens) {
        if (!Object.prototype.hasOwnProperty.call(tokens, token)) continue;
        var id = msg.doc + '|' + token;
        slices[id] = slices[id] || { url: tokens[token] };
        slices[id].url = tokens[token];
      }
      done({ chunked: !!docs[msg.doc].chunks, chunkCount: docs[msg.doc].chunks ? docs[msg.doc].chunks.count : 0 });
      return;
    }
    if (msg.op === 'expand') { onExpand(msg).then(done, fail); return; }
    if (msg.op === 'find') { onFind(msg).then(done, fail); return; }
    if (msg.op === 'params') { onParams(msg).then(done, fail); return; }
    if (msg.op === 'drop') { delete docs[msg.doc]; done(true); return; }
    fail(new Error('unknown op ' + msg.op));
  } catch (err) {
    fail(err);
  }
};
`;

// The composite key a tree row and the worker agree on. The exporter's
// own `key` on an index component is "Category|Group|Name" with no
// section, so tree rows under Existing / Demolition / Disabled would
// collide with the live ones; this adds the section tag and is computed
// identically on both sides.
export const SECTION_TAGS = { live: 'S', existing: 'E', demolition: 'D', disabled: 'X' };

export function recordKey(section, category, group, name) {
  return `${section}|${category}|${group}|${name}`;
}

let worker = null;
let seq = 0;
const pending = new Map();

function ensureWorker() {
  if (worker) return worker;
  if (typeof Worker === 'undefined' || typeof Blob === 'undefined') return null;
  try {
    const url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
    worker = new Worker(url);
    worker.onmessage = (e) => {
      const { id, ok, payload, error } = e.data || {};
      const entry = pending.get(id);
      if (!entry) return;
      pending.delete(id);
      if (ok) entry.resolve(payload);
      else entry.reject(new Error(error || 'record worker failed'));
    };
    worker.onerror = (e) => {
      // A worker that died takes every outstanding question with it;
      // callers fall back to their own path rather than hanging.
      pending.forEach((entry) => entry.reject(new Error(e.message || 'record worker crashed')));
      pending.clear();
      worker = null;
    };
  } catch (e) {
    worker = null;
  }
  return worker;
}

function ask(message) {
  const w = ensureWorker();
  if (!w) return Promise.reject(new Error('workers unavailable'));
  const id = ++seq;
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    w.postMessage({ ...message, id });
  });
}

// Tell the worker where a document's records and parameter slices live.
// Nothing is fetched until a question needs them.
// `chunks` (optional) is the index's recordChunks plus the chunk urls:
// { hash, count, byKey, probe, urls: { idx: url } }. Resolves to
// { chunked, chunkCount } so the page can tell which path a document took.
export function registerDocument(docId, url, paramSlices, chunks) {
  return ask({ op: 'register', doc: docId, url, slices: paramSlices || {}, chunks: chunks || null }).catch(() => false);
}

// The chunk description for one document, or null when the index does not
// describe chunks or a listed chunk file is missing from zipInfo.
export function chunkSpecFor(index, set, base) {
  const rc = index && index.recordChunks;
  if (!rc || !set || !set.treeChunks) return null;
  const count = Number(rc.count) || 0;
  if (count < 1) return null;
  const urls = {};
  for (let i = 0; i < count; i++) {
    if (!set.treeChunks[i]) return null;
    urls[i] = `${base}/${set.treeChunks[i]}`;
  }
  return { hash: rc.hash, size: rc.size, count, byKey: rc.byKey || {}, probe: rc.probe || null, urls };
}

// Every element under one aggregated component row.
// `expectedTotal` is the index's count for the row; a chunked document
// answers with a page and that total, and says whether its uid list is
// the whole row (`uidsComplete`).
export function expandComponent(docId, key, expectedTotal) {
  return ask({ op: 'expand', doc: docId, key, expectedTotal: expectedTotal || 0 });
}

// The record behind a UniqueId, with the row key it belongs to.
export function findRecord(docId, uid) {
  return ask({ op: 'find', doc: docId, uid });
}

// One element's parameter row, from the per-category slice.
export function findParams(docId, uid, token, fallbackToken) {
  return ask({ op: 'params', doc: docId, uid, token, fallbackToken });
}

// Did the worker refuse a record file for its size? The message it
// rejected with starts with the marker and names the size, e.g.
// "RecordFileTooLarge: 49 MB compressed".
export const RECORD_TOO_LARGE = 'RecordFileTooLarge';
export function isRecordTooLarge(err) {
  return !!err && typeof err.message === 'string' && err.message.indexOf(RECORD_TOO_LARGE) === 0;
}
export function recordTooLargeMessage(docName, err) {
  const size = isRecordTooLarge(err) ? err.message.slice(RECORD_TOO_LARGE.length + 2) : '';
  return `${docName || 'This document'}: the record file is too large to open in the browser`
    + (size ? ` (${size})` : '') + '. Its element details are unavailable until it is re-exported.';
}

export function isWorkerAvailable() {
  return !!ensureWorker();
}

// Tests drive the module directly; the Blob worker is not available in
// jsdom, so they inject a stub instead.
export function __setWorkerForTests(stub) {
  worker = stub;
  if (stub) {
    stub.onmessage = (e) => {
      const { id, ok, payload, error } = e.data || {};
      const entry = pending.get(id);
      if (!entry) return;
      pending.delete(id);
      if (ok) entry.resolve(payload);
      else entry.reject(new Error(error || 'record worker failed'));
    };
  }
  pending.clear();
}

export { WORKER_SOURCE };
